/**
 * Backup / Restore — settings sub-page.
 *
 * Provides BizOP data export (JSON backup), import (restore from backup),
 * InstantDB cloud migration, and a "clear all data" escape hatch.
 */

import { createFileRoute } from "@tanstack/react-router";
import {
  CheckCircle2,
  AlertTriangle,
  ChevronRight,
  ChevronLeft,
  Loader2,
  Database,
  Info,
  Download,
  Download as HardDriveDownload,
  Upload as HardDriveUpload,
  RotateCcw,
  FileText as FileJson,
  Trash2,
} from "lucide-react";
import { useCallback, useRef, useState } from "react";

import { Button, Badge, cn, toast } from "@bizop/ui-kit";

import { PageHeader } from "../../../components/PageHeader";
import { useHasPermission } from "../../../lib/permissions";
import {
  collectAllMockData,
  deserializeExport,
  downloadExport,
  restoreAllMockData,
  countRecords,
  persistToLocalStorage,
  clearAllData,
  type BizopExportFile,
  type RestoreResult,
} from "../../../lib/export/bizopData";
import {
  isInstantDbEnabled,
  seedFromLocalStorage,
  wipeAllEntities,
  pushEntitiesToInstantDb,
  isDbEntityName,
  type DbEntityName,
  type MigrationReport,
  type MigrationProgress,
} from "../../../lib/db";

export const Route = createFileRoute("/_app/settings/import-export")({
  component: ImportExportPage,
});

/* ================================================================== */
/*  Root component                                                     */
/* ================================================================== */

function ImportExportPage() {
  const canWrite = useHasPermission("settings.write");

  if (!canWrite) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader
          title="Backup / Restore"
          description="BizOP data export and import utilities."
        />
        <div className="rounded-lg border border-(--border-subtle) bg-(--bg-surface) p-8 text-center">
          <AlertTriangle
            aria-hidden
            className="mx-auto mb-3 h-8 w-8 text-(--text-tertiary)"
          />
          <h2 className="text-[14px] font-semibold text-(--text-primary)">
            Access denied
          </h2>
          <p className="mt-1 text-[13px] text-(--text-secondary)">
            You need the <span className="font-medium">settings.write</span>{" "}
            permission to access export and import features.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Backup / Restore"
        description="Export BizOP data as a JSON backup, or restore from a previous export."
      />

      {/* ── BizOP Data Export ── */}
      <BizopExportSection />

      {/* ── BizOP Data Import ── */}
      <BizopImportSection />

      {/* ── InstantDB Migration ── */}
      <InstantDbMigrationSection />

      {/* ── Clear All Data ── */}
      <ClearAllDataSection />
    </div>
  );
}

/* ================================================================== */
/*  BizOP Data Export                                                   */
/* ================================================================== */

function BizopExportSection() {
  const [exporting, setExporting] = useState(false);
  const [lastExport, setLastExport] = useState<{
    at: string;
    counts: Record<string, number> & { totalRecords: number };
  } | null>(null);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      // Yield so spinner can paint.
      await new Promise((r) => setTimeout(r, 0));
      const data = collectAllMockData();
      const counts = countRecords(data);
      downloadExport(data);
      setLastExport({ at: data.exportedAt, counts });
      toast.success("Export downloaded", {
        description: `${counts.totalRecords.toLocaleString()} records across ${Object.keys(data.stores).length} stores.`,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      toast.error("Export failed", { description: msg });
    } finally {
      setExporting(false);
    }
  }, []);

  /** Human-readable store labels. */
  const STORE_LABELS: Record<string, string> = {
    sales: "Sales & Dealers",
    invoicing: "Invoicing & Payments",
    inventory: "Inventory & Warehouses",
    gl: "General Ledger",
    finance: "Finance & Expenses",
    payables: "Payables",
    catalog: "Catalog & Products",
    pricing: "Pricing",
    dealerAreas: "Dealer Areas",
    roles: "Roles & Users",
    workforce: "Workforce & HR",
    production: "Production",
    notifications: "Notifications",
    attachments: "Attachments",
    audit: "Audit Log",
    dashboard: "Dashboard Widgets",
    analysis: "Analysis Data",
  };

  return (
    <section className="rounded-lg border border-(--border-subtle) bg-(--bg-surface) p-5">
      <div className="mb-4 flex items-start gap-3">
        <HardDriveDownload className="mt-0.5 h-5 w-5 shrink-0 text-(--accent)" />
        <div className="min-w-0 flex-1">
          <h2 className="text-[14px] font-semibold tracking-tight text-(--text-primary)">
            Export BizOP Data
          </h2>
          <p className="mt-0.5 text-[12px] text-(--text-secondary)">
            Download all mock data as a JSON file. Use this to transfer data to
            another account or create a backup before testing imports.
          </p>
        </div>
      </div>

      {/* Export action */}
      <div className="flex items-center gap-3">
        <Button type="button" onClick={handleExport} disabled={exporting}>
          {exporting ? (
            <>
              <Loader2 aria-hidden className="mr-1.5 h-4 w-4 animate-spin" />
              Exporting...
            </>
          ) : (
            <>
              <Download aria-hidden className="mr-1.5 h-4 w-4" />
              Download Export
            </>
          )}
        </Button>

        {lastExport && (
          <span className="text-[12px] text-(--text-tertiary)">
            Last export: {new Date(lastExport.at).toLocaleTimeString()} —{" "}
            <span className="font-mono tabular-nums">
              {lastExport.counts.totalRecords.toLocaleString()}
            </span>{" "}
            records
          </span>
        )}
      </div>

      {/* Last export breakdown */}
      {lastExport && (
        <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
          {Object.entries(lastExport.counts)
            .filter(([k]) => k !== "totalRecords")
            .sort(([, a], [, b]) => b - a)
            .map(([storeName, count]) => (
              <div
                key={storeName}
                className="flex items-center justify-between rounded-md border border-(--border-subtle) bg-(--bg-canvas) px-3 py-2"
              >
                <span className="text-[11px] text-(--text-secondary)">
                  {STORE_LABELS[storeName] ?? storeName}
                </span>
                <span className="font-mono text-[12px] font-medium tabular-nums text-(--text-primary)">
                  {count.toLocaleString()}
                </span>
              </div>
            ))}
        </div>
      )}

      {/* Info note */}
      <div className="mt-4 flex items-start gap-2 rounded-md border border-(--border-subtle) bg-(--bg-canvas) px-4 py-3">
        <Info aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-(--text-tertiary)" />
        <p className="text-[12px] text-(--text-secondary)">
          The export includes all 17 data stores (dealers, invoices, inventory,
          GL, finance, catalog, pricing, workforce, and more). BigInt values are
          preserved through a tagged JSON format.
        </p>
      </div>
    </section>
  );
}

/* ================================================================== */
/*  BizOP Data Import                                                   */
/* ================================================================== */

type BizopImportStep = "idle" | "previewing" | "importing" | "done" | "error";

function BizopImportSection() {
  const [step, setStep] = useState<BizopImportStep>("idle");
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<{
    data: BizopExportFile;
    counts: Record<string, number> & { totalRecords: number };
  } | null>(null);
  const [restoreResult, setRestoreResult] = useState<RestoreResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [warningsOpen, setWarningsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const STORE_LABELS: Record<string, string> = {
    sales: "Sales & Dealers",
    invoicing: "Invoicing & Payments",
    inventory: "Inventory & Warehouses",
    gl: "General Ledger",
    finance: "Finance & Expenses",
    payables: "Payables",
    catalog: "Catalog & Products",
    pricing: "Pricing",
    dealerAreas: "Dealer Areas",
    roles: "Roles & Users",
    workforce: "Workforce & HR",
    production: "Production",
    notifications: "Notifications",
    attachments: "Attachments",
    audit: "Audit Log",
    dashboard: "Dashboard Widgets",
    analysis: "Analysis Data",
  };

  /** Parse the selected file and show a preview. */
  const handleFile = useCallback(async (f: File) => {
    if (!f.name.toLowerCase().endsWith(".json")) {
      toast.error("Invalid file type", {
        description: "Please select a .json file exported from BizOP.",
      });
      return;
    }
    setFile(f);
    setStep("previewing");
    setError(null);
    setRestoreResult(null);

    try {
      const text = await f.text();
      const data = deserializeExport(text);

      if (data.version !== 1) {
        throw new Error(`Unsupported version: ${data.version}`);
      }
      if (data.app !== "bizop-admin") {
        throw new Error(
          `This file was exported from "${data.app}", not BizOP Admin.`,
        );
      }

      const counts = countRecords(data);
      setPreview({ data, counts });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      setStep("error");
      setError(msg);
      toast.error("Invalid export file", { description: msg });
    }
  }, []);

  /** Live progress reporter for the InstantDB push phase. */
  const [pushProgress, setPushProgress] = useState<MigrationProgress | null>(
    null,
  );

  /** Execute the import — replaces all mock data and persists to localStorage. */
  const handleImport = useCallback(async () => {
    if (!preview) return;
    setStep("importing");
    setPushProgress(null);

    try {
      await new Promise((r) => setTimeout(r, 0));
      const result = restoreAllMockData(preview.data);
      persistToLocalStorage();

      // If InstantDB is configured, also push every entity-typed array from
      // the import into the cloud. We walk the nested stores object, map
      // each `mockXxx` key to its DbEntityName (`xxx`), and call the shared
      // helper. Non-entity keys (settings singletons, dashboard widgets,
      // analysis aggregates, PERMISSION_GROUPS, DEPARTMENTS, etc.) are
      // skipped automatically because they don't appear in DB_ENTITY_NAMES.
      if (isInstantDbEnabled()) {
        const pushPlan = collectEntityPushPlan(preview.data);
        for (let idx = 0; idx < pushPlan.length; idx++) {
          const { entityName, rows } = pushPlan[idx]!;
          await pushEntitiesToInstantDb(
            entityName,
            rows,
            (written, total) => {
              setPushProgress({
                entityName,
                entityIndex: idx,
                totalEntities: pushPlan.length,
                rowsWritten: written,
                rowsPlanned: total,
                phase: "migrate",
              });
            },
          );
        }
      }

      setRestoreResult(result);
      setStep("done");
      setPushProgress(null);
      toast.success("Import complete", {
        description: `Restored ${result.totalRestored.toLocaleString()} records. Data saved to browser${isInstantDbEnabled() ? " and InstantDB" : ""}.`,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      setStep("error");
      setError(msg);
      setPushProgress(null);
      toast.error("Import failed", { description: msg });
    }
  }, [preview]);

  /** Reset to idle. */
  const reset = useCallback(() => {
    setStep("idle");
    setFile(null);
    setPreview(null);
    setRestoreResult(null);
    setError(null);
    setWarningsOpen(false);
    setPushProgress(null);
  }, []);

  return (
    <section className="rounded-lg border border-(--border-subtle) bg-(--bg-surface) p-5">
      <div className="mb-4 flex items-start gap-3">
        <HardDriveUpload className="mt-0.5 h-5 w-5 shrink-0 text-(--accent)" />
        <div className="min-w-0 flex-1">
          <h2 className="text-[14px] font-semibold tracking-tight text-(--text-primary)">
            Import BizOP Data
          </h2>
          <p className="mt-0.5 text-[12px] text-(--text-secondary)">
            Load a previously exported JSON file to restore all mock data.
            This <strong>replaces</strong> all current data in every store.
          </p>
        </div>
      </div>

      {/* ── Idle: file picker ── */}
      {step === "idle" && (
        <>
          <div
            onClick={() => inputRef.current?.click()}
            onDrop={(e) => {
              e.preventDefault();
              const f = e.dataTransfer.files[0];
              if (f) handleFile(f);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = "copy";
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                inputRef.current?.click();
              }
            }}
            className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-(--border-subtle) bg-(--bg-canvas) p-8 transition-colors hover:border-(--accent) hover:bg-(--bg-hover)"
          >
            <FileJson aria-hidden className="h-8 w-8 text-(--text-tertiary)" />
            <p className="text-[13px] font-medium text-(--text-primary)">
              Drop a BizOP export file here
            </p>
            <p className="text-[12px] text-(--text-tertiary)">
              or click to browse — accepts .json files
            </p>
          </div>
          <input
            ref={inputRef}
            type="file"
            accept=".json"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
              e.target.value = "";
            }}
          />
        </>
      )}

      {/* ── Previewing: show counts + confirm ── */}
      {step === "previewing" && preview && (
        <div className="flex flex-col gap-4">
          {/* File info */}
          <div className="flex items-center gap-3 rounded-md border border-(--border-subtle) bg-(--bg-canvas) px-4 py-3">
            <FileJson aria-hidden className="h-5 w-5 shrink-0 text-(--accent)" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] font-medium text-(--text-primary)">
                {file?.name}
              </p>
              <p className="text-[12px] text-(--text-tertiary)">
                Exported {new Date(preview.data.exportedAt).toLocaleString()} —{" "}
                <span className="font-mono tabular-nums">
                  {preview.counts.totalRecords.toLocaleString()}
                </span>{" "}
                total records
              </p>
            </div>
            <Badge variant="neutral">.json</Badge>
          </div>

          {/* Per-store breakdown */}
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
            {Object.entries(preview.counts)
              .filter(([k]) => k !== "totalRecords")
              .sort(([, a], [, b]) => b - a)
              .map(([storeName, count]) => (
                <div
                  key={storeName}
                  className="flex items-center justify-between rounded-md border border-(--border-subtle) bg-(--bg-canvas) px-3 py-2"
                >
                  <span className="text-[11px] text-(--text-secondary)">
                    {STORE_LABELS[storeName] ?? storeName}
                  </span>
                  <span className="font-mono text-[12px] font-medium tabular-nums text-(--text-primary)">
                    {count.toLocaleString()}
                  </span>
                </div>
              ))}
          </div>

          {/* Warning */}
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-900/50 dark:bg-amber-950/20">
            <AlertTriangle
              aria-hidden
              className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400"
            />
            <p className="text-[12px] text-amber-800 dark:text-amber-300">
              This will <strong>replace all current data</strong> in every mock
              store. Existing records will be overwritten. This cannot be undone.
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-between">
            <Button type="button" variant="outline" onClick={reset}>
              <ChevronLeft aria-hidden className="mr-1.5 h-4 w-4" />
              Cancel
            </Button>
            <Button type="button" onClick={handleImport}>
              <RotateCcw aria-hidden className="mr-1.5 h-4 w-4" />
              Replace All Data
            </Button>
          </div>
        </div>
      )}

      {/* ── Importing: spinner + optional InstantDB push progress ── */}
      {step === "importing" && (
        <div className="flex flex-col gap-4 py-6">
          <div className="flex flex-col items-center gap-3">
            <Loader2 aria-hidden className="h-8 w-8 animate-spin text-(--accent)" />
            <p className="text-[13px] font-medium text-(--text-primary)">
              {pushProgress
                ? "Pushing data to InstantDB..."
                : "Restoring data..."}
            </p>
          </div>
          {pushProgress ? <ProgressBar progress={pushProgress} /> : null}
        </div>
      )}

      {/* ── Done: results ── */}
      {step === "done" && restoreResult && (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col items-center gap-3 py-4">
            <CheckCircle2 aria-hidden className="h-10 w-10 text-green-500" />
            <p className="text-[14px] font-semibold text-(--text-primary)">
              Data restored successfully
            </p>
            <p className="font-mono text-[13px] tabular-nums text-(--text-secondary)">
              {restoreResult.totalRestored.toLocaleString()} records across{" "}
              {Object.keys(restoreResult.perStore).length} stores
            </p>
          </div>

          {/* Per-store counts */}
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
            {Object.entries(restoreResult.perStore)
              .sort(([, a], [, b]) => b - a)
              .map(([storeName, count]) => (
                <div
                  key={storeName}
                  className="flex items-center justify-between rounded-md border border-(--border-subtle) bg-(--bg-canvas) px-3 py-2"
                >
                  <span className="text-[11px] text-(--text-secondary)">
                    {STORE_LABELS[storeName] ?? storeName}
                  </span>
                  <span className="font-mono text-[12px] font-medium tabular-nums text-(--text-primary)">
                    {count.toLocaleString()}
                  </span>
                </div>
              ))}
          </div>

          {/* Warnings */}
          {restoreResult.warnings.length > 0 && (
            <div>
              <button
                type="button"
                onClick={() => setWarningsOpen((v) => !v)}
                className="flex items-center gap-1.5 text-[12px] font-medium text-amber-600 hover:text-amber-700"
              >
                <AlertTriangle aria-hidden className="h-3.5 w-3.5" />
                {restoreResult.warnings.length} warning
                {restoreResult.warnings.length !== 1 ? "s" : ""}
                <ChevronRight
                  aria-hidden
                  className={cn(
                    "h-3.5 w-3.5 transition-transform",
                    warningsOpen && "rotate-90",
                  )}
                />
              </button>
              {warningsOpen && (
                <ul className="mt-2 flex flex-col gap-1 rounded-md border border-amber-200 bg-amber-50 p-3 dark:border-amber-900/50 dark:bg-amber-950/20">
                  {restoreResult.warnings.map((w, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2 text-[12px] text-amber-800 dark:text-amber-300"
                    >
                      <span className="mt-0.5 shrink-0">·</span>
                      <span>{w}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div className="flex justify-end">
            <Button
              type="button"
              onClick={() => window.location.reload()}
            >
              Done — Reload
            </Button>
          </div>
        </div>
      )}

      {/* ── Error ── */}
      {step === "error" && (
        <div className="flex flex-col items-center gap-3 py-8">
          <AlertTriangle aria-hidden className="h-8 w-8 text-red-500" />
          <p className="max-w-md text-center text-[13px] text-(--text-secondary)">
            {error ?? "Unknown error."}
          </p>
          <Button type="button" variant="outline" onClick={reset}>
            Try Again
          </Button>
        </div>
      )}
    </section>
  );
}

/* ================================================================== */
/*  InstantDB Migration                                                */
/* ================================================================== */

function InstantDbMigrationSection() {
  const enabled = isInstantDbEnabled();
  const [running, setRunning] = useState<"" | "migrate" | "seed" | "wipe">("");
  const [report, setReport] = useState<MigrationReport | null>(null);
  const [confirmWipe, setConfirmWipe] = useState(false);
  const [progress, setProgress] = useState<MigrationProgress | null>(null);

  const doMigrate = useCallback(
    async (kind: "migrate" | "seed") => {
      setRunning(kind);
      setReport(null);
      setProgress(null);
      try {
        const result = await seedFromLocalStorage({
          includeMockSeeds: kind === "seed",
          onProgress: (p) => setProgress(p),
        });
        setReport(result);
        const errCount = result.errors.length;
        if (errCount === 0) {
          toast.success(
            `Wrote ${result.totalRowsWritten} rows to InstantDB in ${(result.durationMs / 1000).toFixed(1)}s`,
          );
        } else {
          toast.error(
            `Finished with ${errCount} error${errCount === 1 ? "" : "s"} — see report below`,
          );
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        toast.error(`Failed: ${msg}`);
      } finally {
        setRunning("");
        setProgress(null);
      }
    },
    [],
  );

  const doWipe = useCallback(async () => {
    setRunning("wipe");
    setReport(null);
    setProgress(null);
    try {
      const result = await wipeAllEntities({
        onProgress: (p) => setProgress(p),
      });
      setReport(result);
      toast.success(
        `Wiped ${result.totalRowsWritten} rows from InstantDB in ${(result.durationMs / 1000).toFixed(1)}s`,
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`Wipe failed: ${msg}`);
    } finally {
      setRunning("");
      setConfirmWipe(false);
      setProgress(null);
    }
  }, []);

  return (
    <section className="rounded-lg border border-(--border-subtle) bg-(--bg-surface) p-5">
      <div className="flex items-start gap-3">
        <Database
          aria-hidden
          className={cn(
            "mt-0.5 h-5 w-5 shrink-0",
            enabled ? "text-(--success)" : "text-(--text-tertiary)",
          )}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h2 className="text-[14px] font-semibold tracking-tight text-(--text-primary)">
              InstantDB Cloud Database
            </h2>
            <Badge variant={enabled ? "success" : "neutral"}>
              {enabled ? "Connected" : "Not configured"}
            </Badge>
          </div>
          <p className="mt-0.5 text-[12px] text-(--text-secondary)">
            {enabled
              ? "Manage cloud database state. Migrate from local storage when ready, optionally seed with demo data, or wipe everything for a clean start."
              : "Configure VITE_INSTANT_APP_ID in .env.local to enable cloud sync. The app currently runs in localStorage-only mode."}
          </p>
        </div>
      </div>

      {enabled ? (
        <div className="mt-4 flex flex-col gap-3">
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              onClick={() => doMigrate("migrate")}
              disabled={running !== ""}
            >
              {running === "migrate" ? (
                <>
                  <Loader2
                    aria-hidden
                    className="mr-1.5 h-4 w-4 animate-spin"
                  />
                  Migrating…
                </>
              ) : (
                <>
                  <HardDriveUpload aria-hidden className="mr-1.5 h-4 w-4" />
                  Migrate local data
                </>
              )}
            </Button>

            <Button
              type="button"
              variant="outline"
              onClick={() => doMigrate("seed")}
              disabled={running !== ""}
            >
              {running === "seed" ? (
                <>
                  <Loader2
                    aria-hidden
                    className="mr-1.5 h-4 w-4 animate-spin"
                  />
                  Seeding…
                </>
              ) : (
                <>Load demo data</>
              )}
            </Button>

            {!confirmWipe ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => setConfirmWipe(true)}
                disabled={running !== ""}
                className="border-red-300 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-950/20"
              >
                <Trash2 aria-hidden className="mr-1.5 h-4 w-4" />
                Wipe cloud database
              </Button>
            ) : (
              <div className="inline-flex items-center gap-2 rounded-md border border-red-300 bg-red-50/50 px-2 py-1 dark:border-red-800 dark:bg-red-950/20">
                <span className="text-[12px] font-medium text-red-700 dark:text-red-300">
                  Delete every row in InstantDB?
                </span>
                <Button
                  type="button"
                  size="sm"
                  onClick={doWipe}
                  disabled={running !== ""}
                  className="bg-red-600 text-white hover:bg-red-700"
                >
                  {running === "wipe" ? (
                    <>
                      <Loader2
                        aria-hidden
                        className="mr-1 h-3.5 w-3.5 animate-spin"
                      />
                      Wiping…
                    </>
                  ) : (
                    "Yes, wipe"
                  )}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setConfirmWipe(false)}
                  disabled={running !== ""}
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>

          <p className="text-[11px] text-(--text-tertiary)">
            <span className="font-medium text-(--text-secondary)">
              Migrate local data
            </span>{" "}
            uploads only rows you've persisted in this browser.{" "}
            <span className="font-medium text-(--text-secondary)">
              Load demo data
            </span>{" "}
            additionally seeds sample dealers / invoices / products etc. for
            quick exploration.{" "}
            <span className="font-medium text-(--text-secondary)">
              Wipe cloud database
            </span>{" "}
            deletes every row across all 47 entity types — irreversible.
          </p>

          {progress ? <ProgressBar progress={progress} /> : null}
          {report ? <MigrationReportPanel report={report} /> : null}
        </div>
      ) : null}
    </section>
  );
}

function ProgressBar({ progress }: { progress: MigrationProgress }) {
  const pct =
    progress.rowsPlanned > 0
      ? Math.min(
          100,
          Math.round((progress.rowsWritten / progress.rowsPlanned) * 100),
        )
      : Math.min(
          100,
          Math.round((progress.entityIndex / progress.totalEntities) * 100),
        );

  const label =
    progress.phase === "wipe"
      ? "Wiping"
      : progress.phase === "seed"
        ? "Seeding"
        : "Migrating";

  return (
    <div className="rounded-md border border-(--border-subtle) bg-(--bg-canvas) p-3">
      <div className="mb-1.5 flex items-center justify-between text-[11px]">
        <span className="font-medium text-(--text-primary)">
          {label}{" "}
          <span className="font-mono text-(--text-secondary)">
            {progress.entityName}
          </span>{" "}
          <span className="text-(--text-tertiary)">
            ({progress.entityIndex + 1}/{progress.totalEntities})
          </span>
        </span>
        <span className="tabular-nums text-(--text-secondary)">
          {pct}% · {progress.rowsWritten}
          {progress.rowsPlanned > 0 ? ` / ${progress.rowsPlanned}` : ""} rows
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-(--bg-sunken)">
        <div
          className={cn(
            "h-full rounded-full transition-all duration-200",
            progress.phase === "wipe" ? "bg-(--danger)" : "bg-(--accent)",
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function MigrationReportPanel({ report }: { report: MigrationReport }) {
  const entitiesWithData = Object.entries(report.perEntity).filter(
    ([, v]) => v.written > 0 || v.errors.length > 0,
  );
  return (
    <div className="rounded-md border border-(--border-subtle) bg-(--bg-canvas) p-3 text-[12px]">
      <div className="mb-2 flex items-center justify-between">
        <span className="font-medium text-(--text-primary)">
          Migration report
        </span>
        <span className="text-(--text-tertiary)">
          {report.totalRowsWritten} rows · {(report.durationMs / 1000).toFixed(1)}s
        </span>
      </div>
      <ul className="flex max-h-60 flex-col gap-0.5 overflow-y-auto">
        {entitiesWithData.map(([name, v]) => (
          <li
            key={name}
            className="flex items-baseline justify-between gap-2 text-[11px]"
          >
            <span className="font-mono text-(--text-secondary)">{name}</span>
            <span
              className={cn(
                "tabular-nums",
                v.errors.length > 0
                  ? "text-(--danger)"
                  : "text-(--text-primary)",
              )}
            >
              {v.written}
              {v.errors.length > 0 ? ` (${v.errors.length} err)` : ""}
            </span>
          </li>
        ))}
      </ul>
      {report.errors.length > 0 ? (
        <details className="mt-2">
          <summary className="cursor-pointer text-[11px] text-(--danger)">
            View errors ({report.errors.length})
          </summary>
          <ul className="mt-1 flex max-h-40 flex-col gap-0.5 overflow-y-auto text-[10px] text-(--text-tertiary)">
            {report.errors.slice(0, 50).map((e, i) => (
              <li key={i}>· {e}</li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}

/* ================================================================== */
/*  Clear All Data                                                     */
/* ================================================================== */

function ClearAllDataSection() {
  const [confirming, setConfirming] = useState(false);
  const [clearing, setClearing] = useState(false);

  const handleClear = useCallback(async () => {
    setClearing(true);
    try {
      await clearAllData();
      toast.success("All data cleared", {
        description: isInstantDbEnabled()
          ? "Wiped localStorage and InstantDB. Reloading..."
          : "Reloading with empty state...",
      });
      setTimeout(() => window.location.reload(), 300);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error("Failed to clear data", { description: msg });
      setClearing(false);
    }
  }, []);

  return (
    <section className="rounded-lg border border-red-200 bg-(--bg-surface) p-5 dark:border-red-900/40">
      <div className="flex items-start gap-3">
        <Trash2 className="mt-0.5 h-5 w-5 shrink-0 text-red-500" />
        <div className="min-w-0 flex-1">
          <h2 className="text-[14px] font-semibold tracking-tight text-(--text-primary)">
            Clear All Data
          </h2>
          <p className="mt-0.5 text-[12px] text-(--text-secondary)">
            Erase all data from your browser — dealers, invoices, products,
            everything. The app will reload completely empty. This cannot be undone.
          </p>
        </div>
      </div>
      <div className="mt-4">
        {!confirming ? (
          <Button
            type="button"
            variant="outline"
            onClick={() => setConfirming(true)}
            className="border-red-300 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-950/20"
          >
            <Trash2 aria-hidden className="mr-1.5 h-4 w-4" />
            Clear All Data
          </Button>
        ) : (
          <div className="flex items-center gap-3">
            <span className="text-[13px] font-medium text-red-600 dark:text-red-400">
              {isInstantDbEnabled()
                ? "Are you sure? This will wipe both localStorage and InstantDB."
                : "Are you sure?"}
            </span>
            <Button
              type="button"
              onClick={handleClear}
              disabled={clearing}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {clearing ? (
                <>
                  <Loader2
                    aria-hidden
                    className="mr-1.5 h-4 w-4 animate-spin"
                  />
                  Clearing…
                </>
              ) : (
                "Yes, Clear Everything"
              )}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setConfirming(false)}
              disabled={clearing}
            >
              Cancel
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}

/* ================================================================== */
/*  BizopExportFile → InstantDB push plan                              */
/* ================================================================== */

/**
 * Walk a `BizopExportFile`'s nested stores and collect every key whose
 * name resolves to a known DbEntityName, paired with its row array.
 *
 * The export file uses `mockXxx` keys (e.g. `mockDealers`), while
 * InstantDB uses `xxx` (e.g. `dealers`). We strip the `mock` prefix,
 * lowercase the first character, and look the result up in the
 * `isDbEntityName` registry. Anything not in the registry (singleton
 * widgets, settings objects, PERMISSION_GROUPS, analysis aggregates,
 * etc.) is skipped — those don't have InstantDB tables.
 *
 * Returns an array preserving the natural store iteration order so
 * progress reporting matches the order in `restoreAllMockData`.
 */
function collectEntityPushPlan(
  data: BizopExportFile,
): Array<{
  entityName: DbEntityName;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rows: Array<{ _id: string } & Record<string, any>>;
}> {
  const plan: Array<{
    entityName: DbEntityName;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    rows: Array<{ _id: string } & Record<string, any>>;
  }> = [];

  const seen = new Set<DbEntityName>();

  for (const storeData of Object.values(data.stores)) {
    if (storeData === null || typeof storeData !== "object") continue;
    for (const [rawKey, value] of Object.entries(
      storeData as Record<string, unknown>,
    )) {
      if (!Array.isArray(value)) continue;
      const entityKey = mockKeyToEntityName(rawKey);
      if (!entityKey) continue;
      if (!isDbEntityName(entityKey)) continue;
      // Skip duplicate entity entries that appear in multiple stores
      // (e.g. `mockProducts` lives under both `catalog` and `inventory`).
      if (seen.has(entityKey)) continue;
      // Only keep rows that have an `_id`.
      const rows = (value as unknown[]).filter(
        (r): r is { _id: string } & Record<string, unknown> =>
          r !== null &&
          typeof r === "object" &&
          typeof (r as { _id?: unknown })._id === "string",
      );
      if (rows.length === 0) continue;
      seen.add(entityKey);
      plan.push({
        entityName: entityKey,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        rows: rows as any,
      });
    }
  }

  return plan;
}

/**
 * Convert a `mockXxx` export key into the corresponding DbEntityName
 * (`xxx` with the first character lowercased). Returns null for keys
 * that don't follow the `mock` prefix convention.
 */
function mockKeyToEntityName(rawKey: string): string | null {
  if (!rawKey.startsWith("mock")) return null;
  const tail = rawKey.slice(4);
  if (tail.length === 0) return null;
  return tail.charAt(0).toLowerCase() + tail.slice(1);
}
